// Pure logic of the "Por tipo de turno" tab: a court "product" is a sport + a duration (for example
// "Pádel 90 min"), and the back sends one row per product in `info.products`
// (`{sport, durationMinutes, reservations, bookedMinutes, revenue}`, revenue = collected money).
// Grouping happens in the back; here we only filter by sport, derive the per-row numbers (hours,
// average, share) and the labels, so the component stays presentational.
//
// An older back answers without `products`: every helper treats a missing list as "no data".

import { SPORT_LABELS } from "../../constants/courtDomain";
import { fmtHours, fmtInt, fmtMoney, fmtPct0 } from "./statsFormat";

export const ALL_SPORTS = "all";

const sportLabel = (sport) => SPORT_LABELS[sport] || sport || "";
const list = (products) => (Array.isArray(products) ? products : []);

/** "Pádel 90 min"; an unknown sport keeps its raw name, a missing duration is left out. */
export const productLabel = (sport, durationMinutes) =>
  [sportLabel(sport), durationMinutes > 0 ? `${durationMinutes} min` : ""].filter(Boolean).join(" ");

/** Chips of the sport filter: "Todos" plus one per sport present, in order of first appearance. */
export const sportChips = (products) => {
  const seen = [];
  list(products).forEach((p) => {
    if (!seen.includes(p.sport)) seen.push(p.sport);
  });
  return [{ value: ALL_SPORTS, label: "Todos" }, ...seen.map((sport) => ({ value: sport, label: sportLabel(sport) }))];
};

/**
 * Rows of the bars and the table: groups with reservations, optionally narrowed to one sport, sorted
 * by revenue (highest first). `share` is the part of the club's TOTAL collected revenue (all
 * sports), so it does not change when a chip is selected; it is null when nothing was collected.
 */
export const buildProductRows = (products, sport = ALL_SPORTS) => {
  const counted = list(products).filter((p) => p.reservations > 0);
  const total = counted.reduce((sum, p) => sum + (p.revenue || 0), 0);
  return counted
    .filter((p) => sport === ALL_SPORTS || p.sport === sport)
    .map((p) => ({
      key: `${p.sport}|${p.durationMinutes}`,
      sport: p.sport,
      sportLabel: sportLabel(p.sport),
      durationMinutes: p.durationMinutes,
      label: productLabel(p.sport, p.durationMinutes),
      reservations: p.reservations,
      hours: p.bookedMinutes / 60,
      revenue: p.revenue,
      average: p.revenue / p.reservations,
      share: total > 0 ? p.revenue / total : null,
    }))
    .sort((a, b) => b.revenue - a.revenue);
};

/** Text twin of the bars ("Ver tabla"): header and already formatted cells. */
export const productsTable = (rows) => ({
  headers: ["Turno", "Deporte", "Duración", "Reservas", "Horas", "Facturado", "Promedio por reserva", "% del total"],
  rows: rows.map((r) => [
    r.label,
    r.sportLabel,
    r.durationMinutes > 0 ? `${r.durationMinutes} min` : "—",
    fmtInt(r.reservations),
    fmtHours(r.hours),
    fmtMoney(r.revenue),
    fmtMoney(r.average),
    fmtPct0(r.share),
  ]),
});
